import {writable} from 'svelte/store';

import {DefaultRideType, DefaultPaceProfile, RideTypeToRouteType} from '@js/components/modules/constants';


const initState = {
    map: null,

    mapLocation: undefined,
    // Object of {lat:, lng:}
    startPointLocation: undefined,

    currentRideType: window.currentUserRideType || DefaultRideType,

    currentRouteType: RideTypeToRouteType[window.currentUserRideType || DefaultRideType] || 'hike',

    currentPaceProfile: DefaultPaceProfile,

    // Distance in meters
    currentDistance: 0,

    // Duration in seconds
    currentDuration: 0,
    durationComputed: false,

    // Break time in seconds
    currentBreakTime: 0,

    // Speed in km/h
    currentSpeed: 0,
    speedComputed: true,

    currentCalories: 0,

    currentLevel: undefined,

    isochroneType: null,
    isochroneValue: 0,

    // Planner route-generation mode chosen under the search: 'free' | 'isochrone' | 'loop'
    plannerMode: 'free',
    // Loop (round-trip) config picked in the options block; the map's generate button reads it
    roundTripType: 'distance',
    roundTripValue: null,

    // Duration to reach ride start point from user location
    timeToStartDuration: 0,
    // Object of {mode:, lat:, lng:, value:}
    userCurrentLocation: undefined,

    elevationApsis: {
        positiveElevation: null,
        negativeElevation: null,
        minimumAltitude: null,
        maximumAltitude: null,
        gradeAvg: null
    },

    // Technical difficulty (hiking): {level, maxGrade, unpavedRatio} — shown in the route details panel
    technicity: null,

    // Elevation profile points [{lat, lng, elevation, deltaDistance, totalDistance}] once fetched,
    // shared so panels outside the map (e.g. the ride-show waypoints timeline) can locate stops.
    coordinateElevations: null,

    // Current base map style id, so panels outside the map (e.g. planning tools) can react to it
    currentMapStyle: null,

    isSlopePercentageEnabled: false,
    isSurfaceTypeEnabled: false,

    // Waymarked-trails overlay: highlight officially marked hiking routes on the base map
    isWaymarkedTrailsEnabled: false,

    // Prefer paths / avoid car roads (routing safety). Default on for hiking, off for walk/running.
    isPreferPathsEnabled: (window.currentUserRideType || DefaultRideType) === 'hiking',

    // POI mode: clicking on the map adds a point of interest (image) instead of a route point
    isPoiModeEnabled: false,

    // Waypoint mode: clicking adds a numbered stop snapped onto the drawn track
    isWaypointModeEnabled: false,

    // Freehand draw: the user sketches a trace that is matched onto the roads on release
    isFreehandDrawEnabled: false,

    importedPerformedAt: undefined
};

function MapStore(state = initState) {
    const {
        subscribe,
        // set,
        update
    } = writable(
        state
    );

    function updateState(data) {
        Object.entries(data)
            .forEach(([dataName, newData]) => {
                update((storeState) => {
                    storeState[dataName] = newData;

                    return storeState;
                });
            });
    }

    return {
        subscribe,
        get state() {
            return state;
        },
        get map() {
            return state.map;
        },
        set map(map) {
            updateState({map});
        },
        get mapLocation() {
            return state.mapLocation;
        },
        set mapLocation(mapLocation) {
            updateState({mapLocation});
        },
        get startPointLocation() {
            return state.startPointLocation;
        },
        set startPointLocation(startPointLocation) {
            updateState({startPointLocation});
        },
        get currentDistance() {
            return state.currentDistance;
        },
        set currentDistance(currentDistance) {
            updateState({currentDistance});
        },
        get currentDuration() {
            return state.currentDuration;
        },
        set currentDuration(currentDuration) {
            updateState({currentDuration});
        },
        get durationComputed() {
            return state.durationComputed;
        },
        set durationComputed(durationComputed) {
            updateState({durationComputed});
        },
        get currentBreakTime() {
            return state.currentBreakTime;
        },
        set currentBreakTime(currentBreakTime) {
            updateState({currentBreakTime});
        },
        get currentSpeed() {
            return state.currentSpeed;
        },
        set currentSpeed(currentSpeed) {
            updateState({currentSpeed});
        },
        get speedComputed() {
            return state.speedComputed;
        },
        set speedComputed(speedComputed) {
            updateState({speedComputed});
        },
        get currentCalories() {
            return state.currentCalories;
        },
        set currentCalories(currentCalories) {
            updateState({currentCalories});
        },
        get currentLevel() {
            return state.currentLevel;
        },
        set currentLevel(currentLevel) {
            updateState({currentLevel});
        },
        get isochroneType() {
            return state.isochroneType;
        },
        set isochroneType(isochroneType) {
            updateState({isochroneType});
        },
        get isochroneValue() {
            return state.isochroneValue;
        },
        set isochroneValue(isochroneValue) {
            updateState({isochroneValue});
        },
        get plannerMode() {
            return state.plannerMode;
        },
        set plannerMode(plannerMode) {
            updateState({plannerMode});
        },
        get roundTripType() {
            return state.roundTripType;
        },
        set roundTripType(roundTripType) {
            updateState({roundTripType});
        },
        get roundTripValue() {
            return state.roundTripValue;
        },
        set roundTripValue(roundTripValue) {
            updateState({roundTripValue});
        },
        get timeToStartDuration() {
            return state.timeToStartDuration;
        },
        set timeToStartDuration(timeToStartDuration) {
            updateState({timeToStartDuration});
        },
        get userCurrentLocation() {
            return state.userCurrentLocation;
        },
        set userCurrentLocation(userCurrentLocation) {
            updateState({userCurrentLocation});
        },
        get technicity() {
            return state.technicity;
        },
        set technicity(technicity) {
            updateState({technicity});
        },
        get coordinateElevations() {
            return state.coordinateElevations;
        },
        set coordinateElevations(coordinateElevations) {
            updateState({coordinateElevations});
        },
        get currentMapStyle() {
            return state.currentMapStyle;
        },
        set currentMapStyle(currentMapStyle) {
            updateState({currentMapStyle});
        },
        get elevationApsis() {
            return state.elevationApsis;
        },
        set elevationApsis(elevationApsis) {
            updateState({elevationApsis});
        },
        get currentRideType() {
            return state.currentRideType;
        },
        set currentRideType(currentRideType) {
            updateState({currentRideType});
        },
        get currentRouteType() {
            return state.currentRouteType;
        },
        set currentRouteType(currentRouteType) {
            updateState({currentRouteType});
        },
        get currentPaceProfile() {
            return state.currentPaceProfile;
        },
        set currentPaceProfile(currentPaceProfile) {
            updateState({currentPaceProfile});
        },
        get isSlopePercentageEnabled() {
            return state.isSlopePercentageEnabled;
        },
        set isSlopePercentageEnabled(isSlopePercentageEnabled) {
            updateState({isSlopePercentageEnabled});
        },
        get isSurfaceTypeEnabled() {
            return state.isSurfaceTypeEnabled;
        },
        set isSurfaceTypeEnabled(isSurfaceTypeEnabled) {
            updateState({isSurfaceTypeEnabled});
        },
        get isWaymarkedTrailsEnabled() {
            return state.isWaymarkedTrailsEnabled;
        },
        set isWaymarkedTrailsEnabled(isWaymarkedTrailsEnabled) {
            updateState({isWaymarkedTrailsEnabled});
        },
        get isPreferPathsEnabled() {
            return state.isPreferPathsEnabled;
        },
        set isPreferPathsEnabled(isPreferPathsEnabled) {
            updateState({isPreferPathsEnabled});
        },
        get isPoiModeEnabled() {
            return state.isPoiModeEnabled;
        },
        set isPoiModeEnabled(isPoiModeEnabled) {
            updateState({isPoiModeEnabled});
        },
        get isWaypointModeEnabled() {
            return state.isWaypointModeEnabled;
        },
        set isWaypointModeEnabled(isWaypointModeEnabled) {
            updateState({isWaypointModeEnabled});
        },
        get isFreehandDrawEnabled() {
            return state.isFreehandDrawEnabled;
        },
        set isFreehandDrawEnabled(isFreehandDrawEnabled) {
            updateState({isFreehandDrawEnabled});
        },
        get importedPerformedAt() {
            return state.importedPerformedAt;
        },
        set importedPerformedAt(importedPerformedAt) {
            updateState({importedPerformedAt});
        }
    };
}

export const mapStore = MapStore();
